import { addItems } from '@theme/oracura-cart';

// GoKwik's cart opens its drawer on every add request it sees, refused ones
// included: on a desktop an empty "Your Cart (0 items)" slid over the message
// saying the add had failed. When an add fails, the drawer that came up for it
// is closed again so the message is what the shopper sees (as oracura-ui.js
// does for the card buttons).
function closeCartAfterFailedAdd() {
  let tries = 0;
  const check = () => {
    const open = document.querySelector('shadow-dom-container')?.shadowRoot?.querySelector('.sidecart');
    if (open && typeof window.closeGokwikSideCart === 'function') { window.closeGokwikSideCart(); return; }
    if (++tries < 10) setTimeout(check, 150);
  };
  check();
}

class OracuraProduct extends HTMLElement {
  /* The button only earns its place when there is something hidden behind it.
     Clamping is a CSS decision that changes with viewport and with the copy
     itself, so the question is asked of the laid-out element rather than
     guessed from a character count. */
  measureDesc() {
    if (!this.descEl || !this.descMore) return;
    const wasOpen = this.descEl.classList.contains('is-expanded');
    if (wasOpen) this.descEl.classList.remove('is-expanded');
    const clipped = this.descEl.scrollHeight > this.descEl.clientHeight + 1;
    if (wasOpen) this.descEl.classList.add('is-expanded');
    this.descMore.hidden = !clipped;
  }

  connectedCallback() {
    this.lifecycle?.abort();
    this.lifecycle = new AbortController();
    this.variants = this.json('[data-variants]') || [];
    this.prices = this.json('[data-variant-prices]') || {};
    this.variantPhotos = this.json('[data-variant-photos]') || {};
    this.form = this.querySelector('form[action*="/cart/add"]');
    this.idInput = this.querySelector('[data-variant-id]');
    this.qty = this.querySelector('[data-qty-input]');
    this.current = this.variants.find(v => String(v.id) === this.idInput?.value);
    this.flossGiftConfig = this.json('[data-flosser-gift-config]');
    this.flossGiftIds = new Set((this.flossGiftConfig?.variants || []).map(v => Number(v.id)));
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.active = Math.max(0, this.slides.findIndex(s => s.classList.contains('is-active')));
    // Each variant's own slides, in its order, where the gallery is shown a
    // variant at a time (the section works the lists out). Null elsewhere,
    // and every slide stays in play.
    this.gallerySets = this.json('[data-gallery-sets]');
    // The score beside the title links to the reviews band. Where the band
    // shows no reviews (or is not on the template) the score goes too: a
    // count over "No reviews yet" is a number nothing on the page backs. The
    // stylesheet does this before the script runs; this covers a browser
    // without :has().
    const score = this.querySelector('.oracura-pdp__rating[href^="#"]');
    if (score) {
      const band = document.getElementById(score.getAttribute('href').slice(1));
      if (!band || !band.hasAttribute('data-reviews-shown')) score.hidden = true;
    }
    this.descEl = this.querySelector('[data-pdp-desc]');
    this.descMore = this.querySelector('[data-pdp-desc-more]');
    if (this.descMore) {
      this.on(this.descMore, 'click', () => {
        const open = this.descEl.classList.toggle('is-expanded');
        this.descMore.textContent = open
          ? (this.dataset.descLessLabel || 'Show less')
          : (this.dataset.descMoreLabel || 'Show more');
      });
      requestAnimationFrame(() => this.measureDesc());
    }

    this.on(this, 'click', e => this.click(e));
    this.on(this, 'oracura:kit-change', () => this.kitLabels());
    this.on(this, 'change', e => {
      if (e.target.matches('[data-option-value]')) this.select(e.target);
      if (e.target === this.qty) this.normalize();
      if (e.target.matches('[data-selling-plan]')) this.update(this.current, false);
    });
    if (this.form) this.on(this.form, 'submit', e => {
      e.preventDefault();
      if (this.busy || !this.current?.available) return;
      const gift = this.querySelector('[data-gift-modal][data-gift-variant]');
      if (gift && !this.flossGiftIds.has(Number(this.current?.id))) this.openModal(gift); else this.purchase();
    });
    this.dataset.enhanced = 'true';
    this.setupGallery();
    this.setupSpecs();
    this.setupPurchaseBar();
    // A model's hero (both colours, its name) opens the gallery; the first
    // update is the page loading, not a colour being chosen, so it stays.
    // A link to one colour (?variant=) still opens on that colour.
    this.keepHero = this.slides?.[this.active]?.dataset.colour === 'hero' && !new URL(location.href).searchParams.has('variant');
    if (this.flossGiftConfig?.firstPhotos) this.keepHero = false;
    this.update(this.current, false);
    this.keepHero = false;
    this.on(document, 'keydown', e => this.modalKey(e));
    this.on(window, 'popstate', () => {
      const v = this.variants.find(v => String(v.id) === new URL(location.href).searchParams.get('variant'));
      if (!v) return;
      this.check(v);
      this.update(v, false);
    });
  }
  disconnectedCallback() {
    this.lifecycle?.abort();
    this.railObserver?.disconnect();
    clearTimeout(this.purchaseFeedbackTimer);
    this.purchaseHome?.before(this.purchaseBar);
    if (this.addStandIn?.isConnected) { this.addStandIn.replaceWith(this.addButton); this.addButton.hidden = false; }
    this.statusHome?.before(this.purchaseStatus);
    this.purchaseHome?.remove();
    this.statusHome?.remove();
    this.closeModal();
    if (this.specs && this.specsHome) this.specsHome.append(this.specs);
    this.mobileSpecs?.remove();
  }
  on(target, type, fn, options = {}) { target.addEventListener(type, fn, { ...options, signal: this.lifecycle.signal }); }
  json(selector) { try { return JSON.parse(this.querySelector(selector)?.textContent || 'null'); } catch { return null; } }
  setupPurchaseBar() {
    this.purchaseBar = this.querySelector('.oracura-pdp__purchase');
    if (!this.purchaseBar || !this.form) return;
    if (!this.purchaseBar.querySelector('.oracura-pdp__bar-product')) {
      const info = document.createElement('div');
      info.className = 'oracura-pdp__bar-product';
      const image = document.createElement('img');
      image.className = 'oracura-pdp__bar-image';
      image.alt = '';
      image.width = 58; image.height = 58;
      const title = document.createElement('span');
      title.textContent = this.querySelector('h1')?.textContent.trim() || '';
      info.append(image, title);
      const price = document.createElement('div');
      price.className = 'oracura-pdp__bar-price';
      const amount = document.createElement('strong');
      amount.dataset.price = '';
      // The save badge beside the bar's price, as beside the page's. A copy
      // of the page's own, so it carries data-save and the variant handler
      // keeps it in step with the rest.
      const line = document.createElement('span');
      line.className = 'oracura-pdp__bar-amount';
      line.append(amount);
      const save = this.querySelector('.oracura-pdp__discount--inline')?.cloneNode(true);
      if (save) {
        save.classList.add('oracura-pdp__discount--bar');
        line.append(save);
      }
      // And the habit box's "+12% extra off", which stands in for it while add-ons are ticked.
      const extra = this.querySelector('.oracura-pdp__pricing [data-kit-extra]')?.cloneNode(true);
      if (extra) {
        extra.classList.add('oracura-pdp__extra--bar');
        line.append(extra);
      }
      price.append(line);
      const tax = this.querySelector('.oracura-pdp__tax-note');
      if (tax) {
        const note = document.createElement('small');
        note.textContent = tax.textContent.trim();
        price.append(note);
      }
      this.purchaseBar.prepend(info, price);
    }
    this.purchaseHome = document.createComment('Purchase controls');
    this.purchaseBar.before(this.purchaseHome);
    this.purchaseStatus = this.querySelector('[data-product-status]');
    if (this.purchaseStatus) {
      this.statusHome = document.createComment('Purchase feedback');
      this.purchaseStatus.before(this.statusHome);
    }
    /* The real Add button never leaves the form. GoKwik's cart binds its own
       click handler to `form[action='/cart/add'] button[type='submit']`
       whenever its config arrives, which can be before or after this script
       runs. Bound first and then carried out of the form with the bar, its
       handler found no form to read: it posted no variant, Shopify refused the
       add, and the click did nothing. So on desktop the button stays where it
       was, hidden, and the floating bar carries a stand-in that submits the
       form through it. Whichever script runs first, the button it binds to is
       still inside the form.

       The phone bar carries the stand-in too. There the real button stayed in
       the bar, GoKwik's handler took the tap and posted the add itself, and a
       refused add (stock, the per-item limit, a rate limit) showed nothing at
       all: no message, no spinner. Through the stand-in every add, at every
       width, goes through purchase() below, which says what went wrong. */
    this.addButton = this.purchaseBar.querySelector('[type="submit"]');
    if (this.addButton && !this.addStandIn) {
      this.addStandIn = document.createElement('button');
      this.addStandIn.type = 'button';
      this.addStandIn.className = this.addButton.className;
      this.addStandIn.addEventListener('click', () => {
        if (this.addStandIn.disabled) return;
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(this.addButton);
        else this.addButton.click();
      });
    }
    const desktop = matchMedia('(min-width: 761px)');
    if (this.addButton && !this.addStandIn.isConnected) {
      this.addStandIn.disabled = this.addButton.disabled;
      this.addStandIn.textContent = this.addButton.textContent;
      this.addButton.replaceWith(this.addStandIn);
      this.purchaseHome.before(this.addButton);
      this.addButton.hidden = true;
    }
    // The message about an add rides with the bar at every width, just above
    // it: in the page flow it sat under the price, below the fold of a phone
    // with the bar pinned at the foot, so a refused add said nothing visible.
    if (this.purchaseStatus) this.purchaseBar.append(this.purchaseStatus);
    const position = () => {
      this.purchaseBar.classList.toggle('oracura-pdp__purchase--floating', desktop.matches);
      if (desktop.matches) this.append(this.purchaseBar);
      else this.purchaseHome.before(this.purchaseBar);
      if (this.current?.available) this.addButtons().forEach(add => { add.textContent = desktop.matches ? (this.dataset.addLabelDesktop || this.dataset.addLabel) : this.dataset.addLabel; });
      this.combo?.paint?.();
      this.kitLabels();
    };
    this.on(desktop, 'change', position);
    position();
  }
  // The real Add button and its stand-in in the bar: one state for both.
  addButtons() { return [this.addButton || this.querySelector('.oracura-pdp__add'), this.addStandIn].filter(Boolean); }
  message(text = '') { const el = this.querySelector('[data-product-status]'); if (el) { el.textContent = text; el.hidden = !text; } }
  click(e) {
    if (e.target === this.modal) this.closeModal();
    const b = e.target.closest('button, [data-plan-toggle]');
    if (!b || b.disabled) return;
    if (b.matches('[data-prev]')) this.step(-1);
    if (b.matches('[data-next]')) this.step(1);
    if (b.matches('[data-thumb]')) this.show(Number(b.dataset.thumb));
    if (b.matches('[data-qty-minus], [data-qty-plus]')) {
      this.qty.value = this.quantity() + (b.matches('[data-qty-plus]') ? 1 : -1) * Number(this.qty.step || 1); this.normalize();
    }
    if (b.matches('[data-buy-now]')) this.purchase(true);
    if (b.matches('[data-share]')) this.share();
    if (b.matches('[data-plan-toggle]')) {
      const next = b.getAttribute('aria-pressed') !== 'true';
      b.setAttribute('aria-pressed', String(next)); b.classList.toggle('is-selected', next);
    }
    if (b.matches('[data-gift-accept], [data-gift-dismiss]')) {
      const id = b.matches('[data-gift-accept]') ? this.modal?.dataset.giftVariant : null;
      this.closeModal(); this.purchase(false, id);
    }
    const dialog = this.querySelector('[data-zoom-dialog]');
    if (b.matches('[data-gallery-zoom]')) {
      const source = this.slides[this.active]?.querySelector('.oracura-pdp__stage-img');
      if (source && dialog) {
        // A larger cut of the same photograph than the stage's, with the
        // stage's own (already loaded) drawn underneath until it arrives, in a
        // frame sized by the stylesheet: it opened as an empty 24 x 52 box and
        // then at barely the stage's size.
        const image = dialog.querySelector('img'), shown = source.currentSrc || source.src;
        let large = shown;
        try {
          const url = new URL(shown, location.href);
          if (url.searchParams.has('width') || /\/cdn\/shop\//.test(url.pathname)) { url.searchParams.set('width', '2000'); large = url.href; }
        } catch { /* keep the stage's own */ }
        image.removeAttribute('srcset'); image.removeAttribute('sizes'); image.loading = 'eager';
        image.style.backgroundImage = `url("${shown.replace(/"/g, '%22')}")`;
        image.src = large; image.alt = source.alt; dialog.showModal();
      }
    }
    if (b.matches('[data-close-zoom]')) dialog?.close();
  }
  setupGallery() {
    const stage = this.querySelector('.oracura-pdp__stage');
    if (!stage) return;
    this.show(this.active, false);
    this.on(stage, 'keydown', e => {
      if (e.target !== stage || !['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
      e.preventDefault(); this.step(e.key === 'ArrowRight' ? 1 : -1);
    });
    let start;
    this.on(stage, 'pointerdown', e => {
      if (e.pointerType !== 'mouse' && !e.target.closest('button,video,iframe,model-viewer')) start = [e.clientX, e.clientY];
    }, { passive: true });
    this.on(stage, 'pointerup', e => {
      if (!start) return;
      const dx = e.clientX - start[0], dy = e.clientY - start[1]; start = null;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) this.step(dx < 0 ? 1 : -1);
    }, { passive: true });
    this.on(stage, 'pointercancel', () => { start = null; });
    const dialog = this.querySelector('[data-zoom-dialog]');
    if (dialog) this.on(dialog, 'click', e => { if (e.target === dialog) dialog.close(); });
    // Desktop rail: four thumbnails to the stage's height exactly. At their
    // column's width the fourth started 10px above the stage's foot and showed
    // as a sliver, which read as a glitch rather than as "more below".
    const rail = this.querySelector('.oracura-pdp__thumbs');
    if (rail && 'ResizeObserver' in window) {
      const desktop = matchMedia('(min-width: 761px)');
      const fit = () => {
        if (!desktop.matches) { rail.style.removeProperty('--pdp-thumb'); return; }
        const gap = parseFloat(getComputedStyle(rail).rowGap) || 0;
        const size = Math.floor((stage.clientHeight - gap * 3) / 4);
        if (size > 0) rail.style.setProperty('--pdp-thumb', `${size}px`);
      };
      this.railObserver?.disconnect();
      this.railObserver = new ResizeObserver(fit);
      this.railObserver.observe(stage);
      this.on(desktop, 'change', fit);
    }
  }
  step(direction) {
    const order = this.sequence || this.slides.map((_, i) => i);
    const at = Math.max(0, order.indexOf(this.active));
    this.show(order[(at + direction + order.length) % order.length]);
  }
  // Only this variant's slides are in play, in the order given; the thumbnails
  // follow, by flex order, so a pack's own picture can lead its rail.
  filterGallery(list) {
    const order = Array.isArray(list) && list.length ? list : this.slides.map((_, i) => i);
    this.sequence = order;
    this.slides.forEach((s, i) => s.classList.toggle('is-hidden', !order.includes(i)));
    this.thumbs.forEach((t, i) => {
      const at = order.indexOf(Number(t.dataset.thumb ?? i));
      t.classList.toggle('is-hidden', at < 0);
      t.style.order = at < 0 ? '' : String(at);
    });
  }
  check(v) {
    this.querySelectorAll('[data-option-index]').forEach(g => g.querySelectorAll('input').forEach(i => {
      i.checked = i.value === v.options[Number(g.dataset.optionIndex) - 1];
    }));
  }
  show(index, smooth = true) {
    if (!this.slides[index]) return;
    this.active = index;
    this.slides.forEach((s, i) => {
      s.classList.toggle('is-active', i === index); s.inert = i !== index; s.setAttribute('aria-hidden', String(i !== index));
      if (i !== index) {
        s.querySelectorAll('video').forEach(v => v.pause());
        s.querySelectorAll('iframe').forEach(f => {
          f.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
          f.contentWindow?.postMessage({ method: 'pause' }, '*');
        });
      }
    });
    this.thumbs.forEach((t, i) => { t.classList.toggle('is-active', i === index); t.setAttribute('aria-pressed', String(i === index)); });
    const t = this.thumbs[index], strip = t?.parentElement;
    if (strip) {
      const vertical = matchMedia('(min-width:761px)').matches;
      const movement = smooth && !matchMedia('(prefers-reduced-motion:reduce)').matches && !window.OracuraMotion?.paused;
      strip.scrollTo({
        left: vertical ? 0 : t.offsetLeft - (strip.clientWidth - t.offsetWidth) / 2,
        top: vertical ? t.offsetTop - (strip.clientHeight - t.offsetHeight) / 2 : 0,
        behavior: movement ? 'smooth' : 'instant'
      });
    }
    const zoom = this.querySelector('[data-gallery-zoom]');
    if (zoom) zoom.hidden = !this.slides[index].querySelector('.oracura-pdp__stage-img');
  }
  select(changed) {
    const chosen = [];
    this.querySelectorAll('[data-option-index]').forEach(g => { chosen[Number(g.dataset.optionIndex) - 1] = g.querySelector('input:checked')?.value; });
    let v = this.variants.find(v => v.options.every((value, i) => !chosen[i] || value === chosen[i]));
    // Not every colour comes in every model: a pack of two is sold in pairs.
    // The choice just made stands, and the rest moves to the first variant
    // that has it, one in stock before one that is not, keeping as much of the
    // earlier choice as still fits.
    const group = changed?.closest('[data-option-index]');
    if (!v && group) {
      const at = Number(group.dataset.optionIndex) - 1;
      const kept = c => c.options.filter((value, i) => value === chosen[i]).length;
      v = this.variants.filter(c => c.options[at] === changed.value).reduce((best, c) =>
        !best || (c.available && !best.available) || (c.available === best.available && kept(c) > kept(best)) ? c : best, null);
      if (v) this.check(v);
    }
    this.update(v);
  }
  update(v, updateURL = true) {
    const changedVariant = this.current?.id !== v?.id;
    this.current = v;
    const firstPhotos = this.flossGiftConfig?.firstPhotos;
    if (firstPhotos && Object.keys(firstPhotos).length) {
      // OC150, OC180, OC200 and OC300 open on the theme's own photographs (pdp-<model>-<colour>-01);
      // OC450's gallery is its product media, so the config names each colour's first picture.
      const ownPhoto = new RegExp(`pdp-${this.flossGiftConfig.photoModel}-(${Object.keys(firstPhotos).join('|')})-01\\.webp`);
      const photoMedia = this.flossGiftConfig.photoMedia || {};
      // Every water flosser (OC150, OC180, OC200, OC300, OC450) opens on the PRO's photograph, the flosser
      // with its eight tips, while PRO is chosen, and on LITE's own, the flosser with its two tips, while
      // LITE is (OC450's gallery is its product media: photoMedia names both tiers' pictures).
      const pro = v?.options?.some(value => /^pro\b/i.test(String(value)));
      const proPhotos = pro ? this.flossGiftConfig.proPhotos || {} : {};
      const litePhotos = pro ? {} : this.flossGiftConfig.litePhotos || {};
      // Diwali (2 Nov 2026): ahead of those, each colour's Diwali photograph, the flosser with its free
      // MB100 badge, the chosen tier's. OC150-OC300 carry it as the colour's first slide (pdp-<model>-
      // <colour>-00, the PRO's); OC450, whose gallery is its product media, shows it on the chosen tier's
      // own picture and its own photograph on the other tier's (photoTier), the two leading the gallery.
      const tier = pro ? 'pro' : 'lite';
      const diwaliPhotos = this.flossGiftConfig.diwaliPhotos?.[tier] || {};
      const diwaliSlide = new RegExp(`pdp-${this.flossGiftConfig.photoModel}-(${Object.keys(firstPhotos).join('|')})-00\\.webp`);
      const photoTier = this.flossGiftConfig.photoTier || {};
      for (const photo of this.querySelectorAll('[data-slide] .oracura-pdp__stage-img, [data-thumb] img')) {
        const original = photo.dataset.photoOriginal || photo.getAttribute('src') || '';
        const mediaId = photo.closest('[data-media-id]')?.dataset.mediaId;
        const diwaliColour = original.match(diwaliSlide)?.[1];
        const colour = diwaliColour || photoMedia[mediaId] || original.match(ownPhoto)?.[1];
        const own = proPhotos[colour] || litePhotos[colour];
        const leads = diwaliColour || (mediaId in photoTier && photoTier[mediaId] === tier);
        const desired = (leads && diwaliPhotos[colour]) || own || firstPhotos[colour];
        if (!desired) continue;
        photo.dataset.photoOriginal = original;
        // The Diwali, PRO's and LITE's photographs share one frame and fill it like the gallery's own; the
        // older campaign picture is drawn whole.
        photo.style.objectFit = desired === firstPhotos[colour] ? 'contain' : '';
        if (photo.getAttribute('src') !== desired) {
          photo.removeAttribute('srcset');
          photo.removeAttribute('sizes');
          photo.setAttribute('src', desired);
        }
      }
      // The colour and model choices keep their own photographs (Jignesh, 30 Oct): only the
      // gallery's first photo and its thumbnail take the campaign picture.

      // OC450: the chosen colour's two pictures lead, the chosen tier's (the Diwali photograph) first and
      // the other's (the chosen tier's own) second, then the rest of the gallery; the other colour's
      // pictures leave it, as a colour's photographs do on the other flossers' galleries.
      // The rest of the gallery is every other picture, but a picture the admin names for the other tier
      // (10-LITE / 10_-_LITE, 10-PRO / 10_-_PRO: the package contents, one per colour) leaves it, so LITE
      // shows nothing of PRO's and PRO nothing of LITE's (Jignesh, 3 Nov 2026).
      if (Object.keys(photoTier).length) {
        const chosenColour = photoMedia[String(v?.featured_media?.id || '')];
        const liteName = /(^|[^a-z])lite([^a-z]|$)/i, proName = /(^|[^a-z])pro([^a-z]|$)/i;
        // The other tier's alone: a picture that names both (a LITE vs PRO comparison) is for both.
        const otherTier = { test: (name) => (pro ? liteName.test(name) && !proName.test(name) : proName.test(name) && !liteName.test(name)) };
        const fileOf = (slide) => {
          const img = slide.querySelector('.oracura-pdp__stage-img');
          return (img?.dataset.photoOriginal || img?.getAttribute('src') || '').split('?')[0].split('/').pop();
        };
        const lead = [], next = [], rest = [];
        this.slides.forEach((slide, index) => {
          const id = slide.dataset.mediaId;
          if (!(id in photoTier)) { if (!otherTier.test(fileOf(slide))) rest.push(index); }
          else if (photoMedia[id] === chosenColour) (photoTier[id] === tier ? lead : next).push(index);
        });
        if (lead.length) this.filterGallery([...lead, ...next, ...rest]);
      }
    }
    const freeBrush = this.querySelector('[data-flosser-gift-offer]');
    if (freeBrush) freeBrush.hidden = !v?.available || !this.flossGiftIds.has(Number(v.id));
    this.updateMobilePackage?.();
    const selectedOptions = [];
    this.querySelectorAll('[data-option-index]').forEach(group => {
      selectedOptions[Number(group.dataset.optionIndex) - 1] = group.querySelector('input:checked')?.value;
    });
    this.querySelectorAll('.oracura-pdp__model').forEach(label => {
      const image = label.querySelector('.oracura-pdp__model-img');
      const input = label.querySelector('[data-option-value]');
      if (!image || !input) return;
      const modelIndex = Number(label.closest('[data-option-index]').dataset.optionIndex) - 1;
      // Where the colour chosen does not come in this model, the card shows
      // the variant choosing it would select: the model's first in stock.
      const match = this.variants.find(candidate => candidate.options.every((value, index) =>
        value === (index === modelIndex ? input.value : selectedOptions[index]) || (index !== modelIndex && !selectedOptions[index])))
        || this.variants.find(candidate => candidate.available && candidate.options[modelIndex] === input.value)
        || this.variants.find(candidate => candidate.options[modelIndex] === input.value);
      const galleryImage = this.slides.find(slide => String(slide.dataset.mediaId) === String(match?.featured_media?.id))
        ?.querySelector('.oracura-pdp__stage-img');
      // The gallery photo as it was before any campaign picture took its place.
      const source = this.variantPhotos[match?.id] || galleryImage?.dataset.photoOriginal || galleryImage?.getAttribute('src') || match?.featured_image?.src || match?.featured_media?.preview_image?.src;
      image.hidden = !source;
      if (source) {
        const url = new URL(source.startsWith('//') ? `https:${source}` : source, location.href);
        url.searchParams.set('width', '160');
        image.loading = 'eager';
        image.srcset = `${url.href} 160w`;
        image.sizes = '60px';
        if (image.src !== url.href) image.src = url.href;
        image.alt = match.options.join(' — ');
      }
    });
    // A colour that does not come in the chosen model (a single colour beside a
    // pack of pairs) leaves the row until a model that has it is chosen. Where
    // every colour comes in every model nothing is hidden.
    this.querySelectorAll('.oracura-pdp__colours [data-option-value]').forEach(input => {
      const i = Number(input.closest('[data-option-index]').dataset.optionIndex) - 1;
      const label = input.closest('label');
      if (label) label.hidden = !this.variants.some(c => c.options[i] === input.value && c.options.every((value, j) => j === i || !selectedOptions[j] || value === selectedOptions[j]));
    });
    this.querySelectorAll('.oracura-pdp__colour, .oracura-pdp__model').forEach(l => l.classList.toggle('is-selected', !!l.querySelector('input:checked')));
    this.querySelectorAll('[data-option-value]').forEach(input => {
      const i = Number(input.closest('[data-option-index]').dataset.optionIndex) - 1;
      input.closest('label')?.classList.toggle('is-unavailable', !this.variants.some(c => c.available && c.options[i] === input.value));
    });
    if (this.idInput) this.idInput.value = v?.id || '';
    const adds = this.addButtons(), buy = this.querySelector('[data-buy-now]');
    adds.forEach(add => { add.disabled = this.busy || !v?.available; add.textContent = v ? (v.available ? (this.purchaseBar?.classList.contains('oracura-pdp__purchase--floating') ? (this.dataset.addLabelDesktop || this.dataset.addLabel) : this.dataset.addLabel) : this.dataset.soldoutLabel) : window.OracuraLabels?.unavailable || 'Unavailable'; });
    // The phone crumb names the model and the tier or pack ("OC150 PRO",
    // "MB100 PACK OF 4"); the section marks it with the code and which option
    // is the tier, and it follows the switch rather than keeping the first.
    const crumb = this.querySelector('.oracura-pdp__crumb-title[data-crumb-code]');
    if (crumb && v) {
      const tier = String(v.options[Number(crumb.dataset.crumbOption)] || '').split('(')[0].trim().toUpperCase();
      if (tier) crumb.textContent = `${crumb.dataset.crumbCode} ${tier}`;
    }
    // Lite and Pro differ by what is in the box, so the line under the name
    // has to change with the switch. Both are on the element; pick by variant.
    const desc = this.querySelector('[data-pdp-desc]');
    if (desc && v && desc.dataset.blurbLite) {
      const pro = String(v.title || '').toLowerCase().includes('pro');
      const next = pro ? desc.dataset.blurbPro : desc.dataset.blurbLite;
      if (next && next !== desc.textContent) {
        desc.textContent = next;
        // Length changed, so whether it still overflows has to be asked again.
        this.measureDesc?.();
      }
    }

    // Sold out, Buy Now steps aside (Add already says Sold Out) and Notify Me
    // takes its place for the variant on screen; back in stock, Buy Now
    // returns. Both are always in the markup, so a page opened on a sold-out
    // variant gets Buy Now back the moment an in-stock one is chosen.
    const soldOut = !!v && !v.available;
    if (buy) {
      buy.hidden = !v || soldOut;
      buy.disabled = this.busy || !v?.available;
      if (v && !buy.querySelector('.oracura-pdp__buy-price')) {
        buy.textContent = (this.dataset.buyLabel || 'Buy Now') + ' ';
        const price = document.createElement('span');
        price.className = 'oracura-pdp__buy-price';
        buy.appendChild(price);
      }
    }
    const notify = this.querySelector('[data-pdp-notify]');
    if (notify) {
      notify.hidden = !soldOut;
      if (v) {
        notify.dataset.variantId = v.id;
        notify.dataset.variantTitle = v.title === 'Default Title' ? '' : (v.title || '');
      }
      // Already asked about this variant: it says so (oracura-notify.js
      // remembers the request); otherwise it is a live button again.
      if (v && window.OracuraNotify?.isNotified(v.id)) window.OracuraNotify.markDone(notify);
      else {
        notify.classList.remove('is-notified');
        notify.disabled = !!this.busy;
        const text = notify.querySelector('[data-notify-text]');
        if (text && notify.dataset.notifyLabel) text.textContent = notify.dataset.notifyLabel;
      }
    }
    this.purchaseBar?.classList.toggle('is-sold-out', soldOut);
    if (!v) { this.message(window.OracuraLabels?.unavailable || 'This combination is unavailable.'); return; }
    this.message();
    const p = this.prices[v.id];
    const planSelect = this.querySelector('[data-selling-plan]');
    if (planSelect) {
      const selectedPlan = planSelect.value;
      planSelect.replaceChildren();
      if (this.dataset.requiresSellingPlan !== 'true') planSelect.add(new Option(planSelect.dataset.oneTime, ''));
      (p?.plans || []).forEach(plan => planSelect.add(new Option(plan.name + ' — ' + plan.price, String(plan.id))));
      if ([...planSelect.options].some(option => option.value === selectedPlan)) planSelect.value = selectedPlan;
      if (this.dataset.requiresSellingPlan === 'true' && !planSelect.options.length) {
        adds.forEach(add => { add.disabled = true; });
        if (buy) buy.disabled = true;
      }
    }
    const allocation = p?.plans?.find(plan => String(plan.id) === planSelect?.value);
    // The section hands every variant's prices over already formatted (the
    // store's format, no ".00"); this only covers a variant missing from them.
    const money = n => new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { style: 'currency', currency: this.dataset.currency || 'INR', minimumFractionDigits: n % 100 ? 2 : 0 }).format(n / 100);
    this.querySelectorAll('[data-price], .oracura-pdp__buy-price').forEach(el => { el.textContent = allocation?.price || p?.price || money(v.price); });
    const salePrice = allocation?.amount ?? v.price;
    const sale = v.compare_at_price > salePrice, mrp = this.querySelector('[data-mrp]');
    if (mrp) { mrp.hidden = !sale; if (sale) mrp.querySelector('[data-mrp-value]').textContent = p?.compare || money(v.compare_at_price); }
    this.querySelectorAll('[data-save]').forEach(save => { save.hidden = !sale; if (sale) save.querySelector('[data-save-value]').textContent = Math.round((v.compare_at_price - salePrice) * 100 / v.compare_at_price); });
    if (this.qty && p?.quantity_rule) {
      this.qty.min = p.quantity_rule.min || 1; this.qty.step = p.quantity_rule.increment || 1;
      /* Whichever ceiling comes first: the variant’s own quantity rule, or
         what is left in stock. Stock is only sent for a variant Shopify
         tracks and will not sell past, so null here means there is no
         ceiling to apply rather than none left. */
      /* Three possible ceilings: the variant’s own quantity rule, what is
         left in stock, and the most the store will take of any one item.
         The lowest wins. Stock is only sent for a variant Shopify tracks
         and will not sell past, so null means no ceiling from stock rather
         than none left. */
      const perItem = Number(this.dataset.maxPerItem);
      const ceilings = [p.quantity_rule.max, p.stock, perItem].filter(n => typeof n === 'number' && n > 0);
      if (ceilings.length) this.qty.max = Math.min(...ceilings); else this.qty.removeAttribute('max');
    }
    this.normalize();
    if (this.gallerySets) this.filterGallery(this.gallerySets[v.id]);
    const index = this.slides.findIndex(s => String(s.dataset.mediaId) === String(v.featured_media?.id));
    // A pack in a variant-at-a-time gallery has no slide of its own: the
    // gallery shows its colours' photographs. The purchase bar still shows
    // the pack, from the variant's own picture.
    let packPicture = null;
    // After an add the variant is the same one: the gallery stays on the
    // photograph the shopper was looking at (holdGallery, set by purchase).
    const move = !this.holdGallery;
    if (index >= 0) { if (move) this.show(index, false); }
    else {
      // Theme-shipped galleries carry no media ids: their slides are grouped by
      // colour instead, so jump to the first slide of the colour just chosen.
      const wanted = (v.options || []).map((o) => String(o).trim().toLowerCase());
      const byColour = this.slides.findIndex((s) => s.dataset.colour && wanted.includes(s.dataset.colour));
      if (byColour >= 0) { if (!this.keepHero && move) this.show(this.sameSpot(byColour), false); }
      else if (this.gallerySets) {
        packPicture = v.featured_media?.preview_image?.src || v.featured_image?.src || null;
        // Open on the pack's first photograph, as its link does, rather than
        // staying on whichever one the last choice left showing.
        if (this.sequence?.length && move) this.show(this.sequence[0], false);
      }
    }
    if (this.sequence && !this.sequence.includes(this.active)) this.show(this.sequence[0], false);
    const barImage = this.querySelector('.oracura-pdp__bar-image');
    const source = (index >= 0 ? this.slides[index] : this.slides[this.active])?.querySelector('.oracura-pdp__stage-img');
    const barSource = packPicture || source?.getAttribute('src');
    if (barImage) {
      barImage.hidden = !barSource;
      if (barSource) {
        const url = new URL(barSource.startsWith('//') ? `https:${barSource}` : barSource, location.href);
        url.searchParams.set('width', '160');
        if (barImage.src !== url.href) barImage.src = url.href;
      }
    }
    if (updateURL && this.dataset.updateUrl === 'true') {
      const url = new URL(location.href); url.searchParams.set('variant', v.id); history.replaceState({}, '', url);
    }
    this.combo?.heroChanged?.(v);
    this.kit?.heroChanged?.(v);
    this.kitLabels();
  }
  get kit() { return this.querySelector('oracura-kit'); }
  // With add-ons ticked, Add says how many go in ("Add 3 to Bag") and Buy Now shows the kit's
  // total; with none, both are the product's own again.
  kitLabels() {
    const v = this.current, kit = this.kit;
    if (!v?.available || this.busy || !kit?.addLabel) return;
    const n = kit.count;
    const floating = this.purchaseBar?.classList.contains('oracura-pdp__purchase--floating');
    const own = floating ? (this.dataset.addLabelDesktop || this.dataset.addLabel) : this.dataset.addLabel;
    // The kit's label follows the button's own word: "Add 3 to Cart" where it says Add to Cart.
    const label = n ? kit.addLabel(n + 1).replace(/\bbag\b/i, /\bcart\b/i.test(own || '') ? 'Cart' : '$&') : own;
    this.addButtons().forEach(add => { add.textContent = label; });
    const price = n ? kit.totalText : this.querySelector('[data-price]')?.textContent;
    if (price) this.querySelectorAll('[data-buy-now] .oracura-pdp__buy-price').forEach(el => { el.textContent = price; });
    this.kitPrices(n ? kit : null);
  }
  // With add-ons ticked, the price row (on the page, and in the bar held at the foot of the
  // screen) is the set's: its total counting up to the new figure with a jump as it lands, the
  // set's MRP struck through and its % off. Untick them all and the product's own come back.
  kitPrices(kit) {
    const v = this.current;
    const prices = [...this.querySelectorAll('[data-price]')];
    if (!kit) {
      if (!this.kitPriced) return;
      this.kitPriced = false;
      this.kitTotal = null;
      // A count still running would write its next figure over the product's price.
      const from = prices.map(el => { cancelAnimationFrame(el.countFrame); return el.dataset.cents; });
      this.querySelectorAll('[data-kit-extra]').forEach(el => { el.hidden = true; delete el.dataset.pct; });
      this.holdGallery = true;
      try { this.update(v, false); } finally { this.holdGallery = false; }
      // And back down to the product's own price, the way the total went up.
      const box = this.kit;
      prices.forEach((el, i) => {
        if (!box || !from[i]) { delete el.dataset.cents; return; }
        el.dataset.cents = from[i];
        box.countTo(el, v.price);
      });
      return;
    }
    const total = kit.total;
    const mrp = (v.compare_at_price > v.price ? v.compare_at_price : v.price) + (kit.addonsMrp || 0);
    const landed = this.kitTotal !== total;
    this.kitPriced = true;
    this.kitTotal = total;
    prices.forEach(el => {
      if (!el.dataset.cents) el.dataset.cents = String(v.price);
      kit.countTo(el, total);
      if (landed) kit.play(el, 'is-kit-bump', 750);
    });
    const off = mrp > total;
    const mrpEl = this.querySelector('[data-mrp]');
    if (mrpEl) { mrpEl.hidden = !off; if (off) mrpEl.querySelector('[data-mrp-value]').textContent = kit.money(mrp); }
    // The box's saving, on top of the sale prices: +4%, +8%, +12% extra, which only grows as
    // things go in. The set's % against MRP would fall below the product's own (its add-ons
    // are marked down less), and read as though adding more cost the shopper.
    const extra = kit.savings ? kit.pct(kit.count) : 0;
    const extras = [...this.querySelectorAll('[data-kit-extra]')];
    extras.forEach(el => {
      el.hidden = !extra;
      if (!extra) return;
      const was = el.dataset.pct;
      el.dataset.pct = String(extra);
      // Included in the total shown, not taken off it again at checkout: "Incl. 4% bundle off".
      el.textContent = `Incl. ${extra}% bundle off`;
      if (was !== String(extra)) kit.play(el, 'is-kit-bump', 750);
    });
    this.querySelectorAll('[data-save]').forEach(save => {
      save.hidden = !off || (extra > 0 && extras.length > 0);
      if (save.hidden) return;
      save.querySelector('[data-save-value]').textContent = Math.round((mrp - total) * 100 / mrp);
      if (landed) kit.play(save, 'is-kit-bump', 750);
    });
  }
  get combo() { return this.querySelector('oracura-combo'); }
  // The same picture in the colour just chosen: on Peach's second photograph, Green opens on its
  // second, not its first. From the opening hero, or past the end of a shorter set, the
  // colour's first. Lite to Pro in the same colour keeps the picture on screen.
  sameSpot(first) {
    const now = this.slides[this.active], from = now?.dataset.colour, to = this.slides[first]?.dataset.colour;
    if (!from || from === 'hero' || !to) return first;
    const pos = this.slides.filter(s => s.dataset.colour === from).indexOf(now);
    const target = this.slides.filter(s => s.dataset.colour === to)[pos];
    return target ? this.slides.indexOf(target) : first;
  }
  quantity() {
    const min = Number(this.qty?.min || 1), max = Number(this.qty?.max || Infinity), step = Number(this.qty?.step || 1);
    const raw = Number(this.qty?.value), n = Number.isFinite(raw) ? raw : min;
    return min + Math.max(0, Math.floor((Math.min(max, Math.max(min, n)) - min) / step)) * step;
  }
  normalize() {
    if (!this.qty) return;
    this.qty.value = this.quantity();
    const minus = this.querySelector('[data-qty-minus]'), plus = this.querySelector('[data-qty-plus]');
    if (minus) minus.disabled = this.quantity() <= Number(this.qty.min || 1);
    if (plus) plus.disabled = !!this.qty.max && this.quantity() + Number(this.qty.step || 1) > Number(this.qty.max);
  }
  async purchase(checkout = false, giftId = null) {
    // The additional warranty is bought on its page, after the warranty form.
    if (this.dataset.formGate) { location.assign(this.dataset.formGate); return; }
    if (this.busy || !this.current?.available || !this.form?.reportValidity()) return;
    if (this.combo && !this.combo.valid()) { this.message(this.combo.problem()); return; }
    let careItem;
    try { careItem = this.querySelector('oracura-care-plan')?.cartItem(); }
    catch (error) { this.message(error.message); return; }
    this.busy = true; this.form.setAttribute('aria-busy', 'true'); this.message();
    clearTimeout(this.purchaseFeedbackTimer);
    this.purchaseBar?.classList.remove('is-purchased');
    this.purchaseBar?.setAttribute('aria-busy', 'true');
    const actions = checkout ? [this.querySelector('[data-buy-now]')].filter(Boolean) : this.addButtons();
    actions.forEach(action => action.classList.add('is-processing'));
    this.combo?.packing?.();
    [...this.querySelectorAll('.oracura-pdp__purchase button'), ...this.addButtons()].forEach(b => { b.disabled = true; });
    // A combo page sells the combo product, one line in the bag, in the colours chosen for each
    // product in it (assets/oracura-combo.js); this product's own variant only drives the page.
    const item = { id: this.combo?.variantId() || Number(this.idInput.value), quantity: this.quantity() }, data = new FormData(this.form), properties = {};
    for (const [key, value] of data) if (key.startsWith('properties[') && key.endsWith(']')) properties[key.slice(11, -1)] = value;
    if (Object.keys(properties).length) item.properties = properties;
    if (data.get('selling_plan') && !this.combo) item.selling_plan = Number(data.get('selling_plan'));
    const items = [item], plan = this.querySelector('[data-plan-toggle][aria-pressed="true"]');
    if (plan?.dataset.planVariant && plan.dataset.sellingPlan) items.push({ id: Number(plan.dataset.planVariant), quantity: 1, selling_plan: Number(plan.dataset.sellingPlan) });
    if (careItem) items.push(careItem);
    if (giftId) items.push({ id: Number(giftId), quantity: 1 });
    // "Complete your routine" add-ons ride in the same request (assets/oracura-kit.js).
    items.push(...(this.kit?.cartItems?.() || []));
    let message, succeeded = false;
    try {
      // The kit's discount code goes on the cart first, so Buy Now reaches checkout with it. The
      // cart keeper (snippets/oracura-kit-codes.liquid) waits until the items are in too, rather
      // than judge a bag that has the code but not yet the product.
      const hold = window.OracuraKitCodes?.hold || (fn => fn());
      const result = await hold(async () => {
        try { await this.kit?.applyDiscount?.(); } catch (error) { /* the add still goes ahead */ }
        return addItems(items, this, { checkout });
      });
      if (result?.cancelled) return;
      succeeded = true;
      this.combo?.added?.();
      // Buy Now has moved on to checkout; a note left over the bar would only
      // be waiting there when the shopper comes back.
      message = checkout ? '' : window.OracuraLabels?.added || 'Added to your bag';
    }
    catch (error) {
      // Shopify's own refusals are written for shoppers ("Only 30 items were
      // added…"). A page answered in place of the cart (a rate limit, the bot
      // check, an outage) surfaced as the parser's "Unexpected token '<' …
      // is not valid JSON"; that, or a network failure, gets the plain words.
      const raw = String(error?.message || '');
      message = error instanceof SyntaxError || error instanceof TypeError || /json|unexpected token|<!doctype|failed to fetch/i.test(raw)
        ? (window.OracuraLabels?.cartError || 'Unable to add this item. Please try again.')
        : raw;
      /* Shopify answers a refused quantity with the number it would have
         allowed — "Only 30 items were added to your cart due to
         availability". That figure is the real ceiling for this variant, and
         it is not one the storefront can work out for itself: the stock this
         page renders counts every location, while the cart counts only what
         can actually be sent. Take the number, hold the box to it, and the
         shopper is only turned away once. */
      const allowed = /only\s+(\d+)\s+item/i.exec(message || '');
      if (allowed && this.qty) {
        this.qty.max = allowed[1];
        this.normalize();
      }
    }
    finally {
      this.busy = false; this.form.removeAttribute('aria-busy');
      if (succeeded) this.kit?.reset?.();
      this.purchaseBar?.removeAttribute('aria-busy'); actions.forEach(action => action.classList.remove('is-processing'));
      this.holdGallery = true;
      try { this.update(this.current, false); } finally { this.holdGallery = false; }
      this.message(message);
      if (!succeeded) closeCartAfterFailedAdd();
      if (succeeded && !checkout) {
        this.purchaseBar?.classList.add('is-purchased');
        this.purchaseFeedbackTimer = setTimeout(() => {
          this.purchaseBar?.classList.remove('is-purchased');
          if (this.purchaseStatus?.textContent === message) this.message();
        }, 2200);
      }
    }
  }
  async share() {
    const url = new URL(this.dataset.url || location.pathname, location.origin);
    if (this.current) url.searchParams.set('variant', this.current.id);
    try {
      if (navigator.share) await navigator.share({ title: this.querySelector('h1')?.textContent, url: url.href });
      else {
        await navigator.clipboard.writeText(url.href);
        // A note, not an error: it sits over the purchase bar, so it goes again.
        const note = window.OracuraLabels?.linkCopied || 'Product link copied';
        this.message(note);
        setTimeout(() => { if (this.purchaseStatus?.textContent === note) this.message(); }, 2500);
      }
    } catch (error) { if (error.name !== 'AbortError') this.message(window.OracuraLabels?.shareError || 'Please copy the link from your address bar.'); }
  }
  setupSpecs() {
    this.specs = this.querySelector('[data-product-specs]'); if (!this.specs) return;
    this.specsHome = this.specs.parentElement; this.mobileSpecs = document.createElement('section');
    const entries = [...this.specs.querySelectorAll('.oracura-pdp__acc')];
    // Titles are matched on their trimmed text: the summary's own whitespace
    // meant /^How To Use/ never matched, and the steps fell out of the page.
    const titleOf = entry => (entry.querySelector('summary')?.textContent || '').replace(/\s+/g, ' ').trim();
    const find = pattern => entries.find(entry => pattern.test(titleOf(entry)));
    const bodyOf = entry => entry?.querySelector('.oracura-pdp__acc-body');
    // One line per list item or paragraph, and per line break inside one.
    const linesOf = el => {
      if (!el) return [];
      const leaves = [...el.querySelectorAll('li, p')].filter(node => !node.querySelector('li, p'));
      return (leaves.length ? leaves.map(node => node.textContent) : [el.textContent])
        .flatMap(text => text.split('\n')).map(text => text.replace(/\s+/g, ' ').trim()).filter(Boolean);
    };
    const packagingEntry = find(/packag/i);
    const packaging = bodyOf(packagingEntry);
    const lines = linesOf(packaging);
    const models = lines.filter(line => /^OC150 (LITE|PRO) Model/i.test(line));
    if (models.length && !this.specs.querySelector('.oracura-pdp__mobile-package')) {
      const labels = this.specs.dataset;
      const item = (text) => { const li = document.createElement('li'); li.textContent = text; return li; };
      const packageInfo = document.createElement('div'); packageInfo.className = 'oracura-pdp__mobile-package';
      const label = document.createElement('strong'); label.textContent = labels.labelPackage || 'Package contains:';
      const list = document.createElement('ul'); const selectedModel = document.createElement('li'); list.append(selectedModel);
      this.updateMobilePackage = () => {
        const pro = this.current?.options.some(value => /^pro\b/i.test(value));
        selectedModel.textContent = models.find(line => (pro ? /^OC150 PRO/i : /^OC150 LITE/i).test(line)) || models[0] || lines[0];
      };
      this.updateMobilePackage();
      lines.filter(line => /charging cable/i.test(line)).forEach(line => list.append(item(line)));
      // The manual keeps the words the section gave it ("Download user manual (PDF)").
      const manual = packaging.querySelector('a[href*=".pdf"]');
      if (manual) { const li = document.createElement('li'); li.append(manual.cloneNode(true)); list.append(li); }
      packageInfo.append(label, list);

      // The four groups the phone canvas draws, each filled only with what
      // belongs under its heading. They used to take whole accordions by a
      // loose title match: "Battery & Charging" held the cleaning steps, the
      // warranty terms and the maker's address, "Nozzles & Compatibility" the
      // advantages of flossing.
      const features = linesOf(bodyOf(find(/^Features/i)));
      const modeLines = features.filter(line => /mode|pressure/i.test(line));
      const batteryLines = features.filter(line => !modeLines.includes(line) && /battery|charg/i.test(line))
        .concat(lines.filter(line => /charging cable/i.test(line)));
      const otherFeatures = features.filter(line => !modeLines.includes(line) && !batteryLines.includes(line));
      const details = lines.findIndex(line => /^Details\b/i.test(line));
      const physical = (details >= 0 ? lines.slice(details + 1) : []).concat(otherFeatures);
      const groups = document.createElement('div'); groups.className = 'oracura-pdp__mobile-spec-groups';
      const createGroup = (title, textLines) => {
        if (!textLines.length) return;
        const group = document.createElement('details'); group.className = 'oracura-pdp__acc';
        const summary = document.createElement('summary'); summary.className = 'oracura-pdp__acc-summary'; summary.textContent = title;
        const body = document.createElement('div'); body.className = 'oracura-pdp__acc-body';
        const groupList = document.createElement('ul'); textLines.forEach(text => groupList.append(item(text)));
        body.append(groupList); group.append(summary, body); groups.append(group);
      };
      createGroup(labels.labelPhysical || 'Physical Specifications', physical);
      createGroup(labels.labelModes || 'Cleaning Modes & Pressure', modeLines);
      createGroup(labels.labelNozzles || 'Nozzles & Compatibility', models);
      createGroup(labels.labelBattery || 'Battery & Charging', batteryLines);
      this.specs.querySelector('.oracura-pdp__specs-title')?.after(packageInfo, groups);
      // Only what the box and the groups now carry steps aside: the packaging
      // list and the feature list. The description, How To Use, the
      // advantages, the warranty and the care notes stay as the store wrote
      // them — on a phone they were hidden with nowhere else to read them.
      [packagingEntry, find(/^Features/i)].filter(Boolean).forEach(entry => { entry.dataset.mobileDuplicate = ''; });
    }
    this.mobileSpecs.className = 'oracura-pdp-mobile-specs'; this.mobileSpecs.dataset.pdpOrder = 'specs';
    const media = matchMedia('(max-width:760px)');
    const move = () => {
      const main = this.closest('main');
      if (media.matches && main) { if (!this.mobileSpecs.isConnected) main.append(this.mobileSpecs); this.mobileSpecs.append(this.specs); }
      else { this.specsHome.append(this.specs); this.mobileSpecs.remove(); }
    };
    move(); this.on(media, 'change', move);
  }
  openModal(modal) {
    this.modal = modal; this.previousFocus = document.activeElement; this.previousOverflow = document.body.style.overflow;
    modal.classList.add('is-open'); modal.setAttribute('aria-hidden', 'false'); document.body.style.overflow = 'hidden'; modal.querySelector('button')?.focus();
  }
  closeModal() {
    if (!this.modal) return;
    this.modal.classList.remove('is-open'); this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = this.previousOverflow || ''; this.previousFocus?.focus(); this.modal = null;
  }
  modalKey(e) {
    if (e.target.matches('[data-plan-toggle]') && ['Enter', ' '].includes(e.key)) { e.preventDefault(); e.target.click(); }
    if (!this.modal) return;
    if (e.key === 'Escape') { e.preventDefault(); this.closeModal(); return; }
    if (e.key !== 'Tab') return;
    const controls = [...this.modal.querySelectorAll('button:not(:disabled),[href],[tabindex="0"]')], first = controls[0], last = controls.at(-1);
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
  }
}
if (!customElements.get('oracura-product')) customElements.define('oracura-product', OracuraProduct);
